import React, { useState, useRef, useEffect } from 'react';
import { Search, MapPin, X } from 'lucide-react';

const LocationSearch = ({ onSelect, onClear, value = '', placeholder = 'Search any location...' }) => {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync with parent value
  useEffect(() => {
    if (value === '') {
      setQuery('');
    }
  }, [value]);

  const fetchSuggestions = async (searchText) => {
    if (!searchText || searchText.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          searchText
        )}.json?country=IN&limit=10&access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        setSuggestions(data.features);
        setIsOpen(true);
      } else {
        setSuggestions([]);
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    // ✅ When user starts typing, clear the selected location
    // This allows the next search to work properly
    if (onClear) {
      onClear();
    }
    
    fetchSuggestions(value);
  };

  const handleSelect = (feature) => {
    const [lng, lat] = feature.center;
    const placeName = feature.place_name;
    
    setQuery(placeName);
    setIsOpen(false);
    setSuggestions([]);
    
    if (onSelect) {
      onSelect({
        name: placeName,
        latitude: lat,
        longitude: lng,
        place: feature,
        district: feature.context?.find(c => c.id.includes('district'))?.text || '',
        region: feature.context?.find(c => c.id.includes('region'))?.text || '',
        country: feature.context?.find(c => c.id.includes('country'))?.text || '',
      });
    }
  };

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setIsOpen(false);
    if (onSelect) {
      onSelect(null);
    }
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0) {
        handleSelect(suggestions[0]);
      }
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative flex-1">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setIsOpen(true)}
          className="w-full pl-10 pr-10 py-2.5 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white text-sm"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
      </div>

      {isOpen && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-xl shadow-xl z-50 max-h-64 overflow-y-auto">
          {suggestions.map((feature, index) => {
            const placeName = feature.place_name;
            const highlightMatch = (text) => {
              if (!query) return text;
              const parts = text.split(new RegExp(`(${query})`, 'gi'));
              return parts.map((part, i) => 
                part.toLowerCase() === query.toLowerCase() ? 
                  <span key={i} className="font-semibold text-primary-600">{part}</span> : 
                  <span key={i}>{part}</span>
              );
            };

            return (
              <div
                key={index}
                onClick={() => handleSelect(feature)}
                className="flex items-start gap-2 px-4 py-2 hover:bg-neutral-50 cursor-pointer transition border-b border-neutral-100 last:border-0"
              >
                <MapPin className="w-4 h-4 text-neutral-400 mt-1 flex-shrink-0" />
                <div>
                  <div className="text-sm text-neutral-800">
                    {highlightMatch(placeName.split(',')[0])}
                  </div>
                  <div className="text-xs text-neutral-500">
                    {placeName.split(',').slice(1).join(',').trim()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LocationSearch;